// src/components/ManageRoles.jsx
//
// "MANAGE ROLES" page (Account > Manage Roles) -- built from the
// mockup screenshot + the finalized Role-Matrix-v4.docx. One row per
// real user (from GET /users, the same data UsersList.jsx shows), one
// checkbox per permission tier column (View / Contributor / Admin /
// Super Admin -- the four tiers from the matrix doc). Ticking a box
// only changes local state; nothing is written until the single Save
// button at the bottom is clicked, which submits every row's current
// selection in one batch call (PUT /users/roles/batch-update).
//
// Per request, this used to be radio buttons (one tier per user, since
// Users.RoleID is a single column) and is now checkboxes so more than
// one box can be ticked per row -- but Users.RoleID is STILL a single
// column (no schema change was made for this), so only one RoleID can
// ever actually be saved per user. When a row has more than one box
// ticked, Save keeps the highest-permission tier among the ones checked
// (View < Contributor < Admin < Super Admin, the same order the tiers
// arrive in from GET /permission-tiers) and that's what actually gets
// written -- see getHighestCheckedTier/handleSave below. This was a
// deliberate simplification: real multi-role support (a user
// simultaneously holding more than one role, with every permission
// check in the app updated to consider all of them) would need a new
// Users<->Roles table and touches security-relevant code throughout the
// app, which wasn't wanted for this pass.
//
// The four tier columns are backed by ordinary rows in the existing
// Role table (the same table Users.RoleID already points at, and the
// same table the older free-form "Role" CRUD page under Master still
// manages) -- GET /permission-tiers find-or-creates them the first
// time this page loads, so no schema change was needed. Any user still
// sitting on a legacy/custom role name from before starts with no box
// checked in this table; picking one and hitting Save moves them onto
// the matrix's tiers going forward.
import React, { useEffect, useMemo, useState } from "react";
import { API_ORIGIN, authFetch } from "../config/apiConfig";

const API_BASE = API_ORIGIN;

export default function ManageRoles() {
  const [users, setUsers] = useState([]);
  const [tiers, setTiers] = useState([]); // [{RoleID, RoleName}] in View/Contributor/Admin/Super Admin order
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  // userId -> array of RoleIDs currently checked in this table (starts
  // out as a single-element array equal to each user's real RoleID
  // whenever that RoleID matches one of the four tiers; left unset
  // otherwise, same as the mockup's blank rows). Checkboxes let more
  // than one tier be ticked per row, but only the highest-permission one
  // actually gets saved -- see the file header comment and handleSave.
  const [selections, setSelections] = useState({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showSavedModal, setShowSavedModal] = useState(false);

  const loadData = () => {
    setLoading(true);
    setLoadError("");
    Promise.all([
      authFetch(`${API_BASE}/users`).then((res) => res.json()),
      authFetch(`${API_BASE}/permission-tiers`).then((res) => res.json()),
    ])
      .then(([usersData, tiersData]) => {
        const userRows = Array.isArray(usersData) ? usersData : [];
        const tierRows = Array.isArray(tiersData) ? tiersData : [];
        setUsers(userRows);
        setTiers(tierRows);

        const tierIds = new Set(tierRows.map((t) => t.RoleID));
        const initial = {};
        userRows.forEach((u) => {
          if (u.RoleID != null && tierIds.has(u.RoleID)) {
            initial[u.id] = [u.RoleID];
          }
        });
        setSelections(initial);
      })
      .catch(() => setLoadError("Could not load users/roles."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = users;
    if (q) {
      rows = rows.filter((u) =>
        [u.UserName, u.Email].filter(Boolean).some((v) => v.toLowerCase().includes(q))
      );
    }
    const sorted = [...rows].sort((a, b) => {
      const cmp = (a.UserName || "").localeCompare(b.UserName || "");
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [users, search, sortDir]);

  const toggleSort = () => setSortDir((d) => (d === "asc" ? "desc" : "asc"));

  // Toggles one tier checkbox for one user -- unlike the old radio
  // behavior, this doesn't clear the row's other checked tiers.
  const handleToggle = (userId, roleId) => {
    setSelections((prev) => {
      const current = prev[userId] || [];
      const next = current.includes(roleId)
        ? current.filter((id) => id !== roleId)
        : [...current, roleId];
      return { ...prev, [userId]: next };
    });
  };

  // tiers arrives from GET /permission-tiers already in View / Contributor
  // / Admin / Super Admin order (least to most permissive) -- see the
  // file header comment. A tier's index in that array is its rank, so
  // the checked RoleID with the highest index is the one that actually
  // gets saved when a row has more than one box ticked.
  const getHighestCheckedTier = (roleIds) => {
    if (!roleIds || roleIds.length === 0) return null;
    let best = roleIds[0];
    let bestRank = tiers.findIndex((t) => t.RoleID === best);
    for (const id of roleIds.slice(1)) {
      const rank = tiers.findIndex((t) => t.RoleID === id);
      if (rank > bestRank) {
        best = id;
        bestRank = rank;
      }
    }
    return best;
  };

  const handleSave = async () => {
    const assignments = Object.entries(selections)
      .map(([userId, roleIds]) => ({ UserID: userId, RoleID: getHighestCheckedTier(roleIds) }))
      .filter((a) => a.RoleID != null);

    if (assignments.length === 0) {
      setSaveError("Pick a tier for at least one user before saving.");
      return;
    }

    setSaving(true);
    setSaveError("");
    try {
      const res = await authFetch(`${API_BASE}/users/roles/batch-update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assignments }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setSaveError(data.error || "Failed to save role assignments.");
        return;
      }

      setShowSavedModal(true);
    } catch (err) {
      setSaveError("Network error while saving role assignments.");
    } finally {
      setSaving(false);
    }
  };

  const handleSavedModalOk = () => {
    setShowSavedModal(false);
    loadData();
  };

  return (
    <>
      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between page-title-head my-3 my-sm-0">
        <div className="w-100 w-sm-auto mb-2 mb-sm-0">
          {/* Per request, this is now a breadcrumb trail ("Cost
              Estimation / {page}"), same pattern and color (#02bc9c,
              16px) as the Cost Estimation wizard's Step1-7 breadcrumbs
              -- replaces the earlier #788CA5 bordered/uppercase title
              treatment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              Manage Roles
            </span>
          </nav>
        </div>
        <div className="w-100 w-sm-auto">
          <div className="app-search">
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <i className="ti app-search-icon text-muted ti-search" />
          </div>
        </div>
      </div>

      {loadError && <div className="alert alert-danger">{loadError}</div>}
      {saveError && <div className="alert alert-danger">{saveError}</div>}

      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              {/* manage-roles-table (new, scoped) -- per request, same
                  treatment as RateCard.jsx/ProjectList.jsx/UsersList.jsx/
                  EmailConfiguration.jsx: row background #f2f2f2 @ 38%
                  Transparency, hover #02bc9c @ 95% Transparency, via
                  themeOverrides.css. Its own class rather than changing
                  .table-custom/.table-hover directly, since those are
                  shared by RoleMatrix.jsx / UsersList.jsx / AIAssistant.jsx
                  and shouldn't change color there too. */}
              <table className="align-middle mb-0 table table-custom table-hover manage-roles-table w-100">
                <thead className="bg-light border-bottom border-top">
                  <tr className="text-dark fw-bold">
                    <th className="px-3 py-3 text-start" role="button" onClick={toggleSort}>
                      Name <span className="text-muted ms-1">{sortDir === "asc" ? "↑" : "↓"}</span>
                    </th>
                    {tiers.map((t) => (
                      <th key={t.RoleID} className="px-3 py-3 text-center">
                        {t.RoleName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={1 + tiers.length} className="text-center text-muted py-4">Loading...</td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={1 + tiers.length} className="text-center text-muted py-4">No users found.</td>
                    </tr>
                  ) : (
                    filteredRows.map((u) => (
                      <tr key={u.id}>
                        <td className="px-3 text-start">{u.UserName || "—"}</td>
                        {tiers.map((t) => (
                          <td key={t.RoleID} className="px-3 text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={(selections[u.id] || []).includes(t.RoleID)}
                              onChange={() => handleToggle(u.id, t.RoleID)}
                              style={{ cursor: "pointer" }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="card-footer border-0 d-flex justify-content-between align-items-center">
              <div className="text-muted fs-13">
                Checking more than one box for a user keeps only the highest tier when saved.
              </div>
              <button type="button" className="btn btn-teal px-4 py-2 text-uppercase fw-semibold" onClick={handleSave} disabled={saving || loading}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Save success confirmation modal (same teal pattern used
          across the app -- e.g. RateCard.jsx / Step4SubElement.jsx) ---- */}
      {showSavedModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="border-0 modal-content text-white" style={{ backgroundColor: "#124d52", borderRadius: 4 }}>
              <div className="text-center modal-body p-4">
                <div className="mb-3">
                  <div className="align-items-center justify-content-center bg-white d-inline-flex rounded-circle" style={{ width: 48, height: 48 }}>
                    <i className="fs-2 text-dark ti ti-circle-check" style={{ fontStyle: "normal", fontWeight: 700 }} />
                  </div>
                </div>
                <h5 className="fs-5 fw-normal mb-4 modal-title">Role assignments saved successfully</h5>
                <div className="d-flex justify-content-center gap-3">
                  <button type="button" className="btn btn-primary" onClick={handleSavedModalOk}>
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
